/**
 * @date: 3/17/22
 * @author: Yan Wang <ywang@max.md>
 * @copy: (C) Copyright 2022 MaxMD Corporation, All Rights Reserved.
 *
 */

import {Cell, Pie, PieChart, ResponsiveContainer, Sector, Tooltip} from "recharts";
import React, {useCallback, useState} from 'react';
import Typography from "@mui/material/Typography";

export const COLORS = ['#ef5350', '#ec407a', '#ab47bc', '#7e57c2',
    '#5c6bc0', '#42a5f5', '#29b6f6', '#26c6da',
    '#26a69a', '#66bb6a', '#9ccc65', '#d4e157',
    '#ffee58', '#ffca28', '#ffa726', '#ff7043',
    '#8d6e63']
export const RADIAN = Math.PI / 180;

export default function BasicPie(props){
    const {data, width, height, others} = props;
    const [activeIndex, setActiveIndex] = useState(null);
    const onPieEnter = useCallback((_, index) => {
        setActiveIndex(index);
    }, [setActiveIndex])
    const onPieLeave = useCallback((_, index) => {
        setActiveIndex(null);
    }, [])

    const renderCustomizedLabel = ({ cx, cy, midAngle, outerRadius, percent, index, fill }) => {
        const sin = Math.sin(-RADIAN * midAngle);
        const cos = Math.cos(-RADIAN * midAngle);
        const sx = cx + (outerRadius + 10) * cos;
        const sy = cy + (outerRadius + 10) * sin;
        const mx = cx + (outerRadius + 30) * cos;
        const my = cy + (outerRadius + 30) * sin;
        const ex = mx + (cos >= 0 ? 1 : -1) * 30;
        const ey = my;
        const textAnchor = cos >= 0 ? "start" : "end";


        return (
            <g>
                <path
                    d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
                    stroke={fill}
                    fill="none"
                />
                <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
                <text
                    x={ex + (cos >= 0 ? 1 : -1) * 12}
                    y={ey + 5} // adjust the text vertically
                    textAnchor={textAnchor}
                    // fill="#777777"
                    fill={data[index].oldData ? '#e57373' : '#777777'}
                >
                    {data[index].name}({`${(percent * 100).toFixed(2)}%`})
                </text>
                {/*<text*/}
                {/*    x={ex + (cos >= 0 ? 1 : -1) * 12}*/}
                {/*    y={ey}*/}
                {/*    dy={18}*/}
                {/*    textAnchor={textAnchor}*/}
                {/*    fill="#999"*/}
                {/*>*/}
                {/*    {`${(percent * 100).toFixed(0)}%`}*/}
                {/*</text>*/}
            </g>
        );
    };

    const renderActiveShape = props => {
        const {
            cx,
            cy,
            innerRadius,
            outerRadius,
            startAngle,
            endAngle,
            midAngle,
            fill
        } = props;
        const sin = Math.sin(-RADIAN * midAngle);
        const cos = Math.cos(-RADIAN * midAngle);
        const sx = cx + (outerRadius - 95) * cos;
        const sy = cy + (outerRadius - 95) * sin;
        return (
            <Sector
                cx={sx}
                cy={sy}
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
                style={{ filter: `drop-shadow(0px 0px 5px ${fill}`}}
            />
        );
    };

    const customizeTooltip = ({payload, active, label}) => {
        if (active) {
            const name = payload[0].name;
            const value = payload[0].value;
            if (name !== 'others') {
                return (
                    <div style={{
                        backgroundColor: "#b3e5fc",
                        padding: "5px 10px",
                        borderRadius: "8px",
                        boxSizing: "border-box",
                        boxShadow: "0 1px 8px rgba(0,0,0,0.5"
                    }}>
                        <span>{`${name}(${value})`}</span>
                    </div>
                );
            } else {
                return (
                    <div style={{
                        backgroundColor: "#b3e5fc",
                        padding: "5px 10px",
                        borderRadius: "8px",
                        boxSizing: "border-box",
                        boxShadow: "0 1px 8px rgba(0,0,0,0.5",
                        fontSize: '12px'
                    }}>
                        <b>Others:</b>
                        {others && others.map( (d) => (
                            <div key={d.name}>
                                <span>{`${d.name}(${d.value})`}</span>
                                <br/>
                            </div>
                        ))}
                    </div>
                );
        }}
        return null;
    }

    // const data = props.data;
    return (<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
        <Typography fontWeight='bold' color={'#777'} marginBottom={3}>Percent of Published Unique Addresses</Typography>
        <ResponsiveContainer width="100%" maxHeight={height} aspect={1} >
            <PieChart>
                <Pie
                    data={data}
                    dataKey="value"
                    label={renderCustomizedLabel}
                    outerRadius= "68%"
                    fill="#8884d8"
                    activeIndex={activeIndex}
                    activeShape={renderActiveShape}
                    onMouseEnter={onPieEnter}
                    onMouseLeave={onPieLeave}
                    // isAnimationActive={false}
                >
                    {data && data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]}/>
                    ))}
                </Pie>
                <Tooltip content={customizeTooltip}/>
            </PieChart>

        </ResponsiveContainer>

    </div>);
}
